import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { StudyViewer } from '../StudyViewer';
import { getStudyById, getUserProfile, getOrganization, getCourseById, logout } from '../../services/firebase';
import { Study, UserProfile, Organization, Course } from '../../types';
import { Icons } from '../ui/Icons';

interface CourseContext {
  courseId: string;
  courseTitle: string;
  isPublic: boolean;
  nextStudy?: {
    id: string;
    title: string;
    coverImage?: string;
  } | null;
}

export const StudyViewerRoute: React.FC = () => {
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [study, setStudy] = useState<Study | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [courseContext, setCourseContext] = useState<CourseContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fromCourse = searchParams.get('from') === 'course';
  const courseId = searchParams.get('courseId');
  const startFresh = searchParams.get('fresh') === 'true';

  useEffect(() => {
    const loadData = async () => {
      if (!studyId) {
        setError('No study ID provided');
        setLoading(false);
        return;
      }

      try {
        // Load study
        const studyData = await getStudyById(studyId);
        if (!studyData) {
          setError('Study not found');
          setLoading(false);
          return;
        }
        setStudy(studyData);

        // Try to load user profile if authenticated
        const userId = localStorage.getItem('userId'); // We'll need to set this in firebase auth
        if (userId) {
          const userProfile = await getUserProfile(userId);
          if (userProfile) {
            setUser(userProfile);
            if (userProfile.orgId) {
              const org = await getOrganization(userProfile.orgId);
              setOrganization(org);
            }
          }
        }

        // Load course context if coming from a course
        // Wrap in try-catch so permission errors don't fail the whole study load
        if (fromCourse && courseId) {
          try {
            const course = await getCourseById(courseId);
            if (course) {
              const studyIds = course.studyIds || [];
              const currentIndex = studyIds.indexOf(studyId);
              let nextStudy = null;

              // Check if there's a next study in the course
              if (currentIndex !== -1 && currentIndex < studyIds.length - 1) {
                const nextStudyId = studyIds[currentIndex + 1];
                try {
                  const nextStudyData = await getStudyById(nextStudyId);
                  if (nextStudyData) {
                    nextStudy = {
                      id: nextStudyData.id,
                      title: nextStudyData.title,
                      coverImage: nextStudyData.coverImage,
                    };
                  }
                } catch (nextStudyErr) {
                  // Next study might not be accessible - continue without it
                  console.warn('Could not load next study:', nextStudyErr);
                }
              }

              setCourseContext({
                courseId: course.id,
                courseTitle: course.title,
                isPublic: course.visibility === 'public',
                nextStudy,
              });
            }
          } catch (courseErr) {
            // Course context failed to load (likely permission error)
            // Continue without course context - study will still work
            console.warn('Could not load course context:', courseErr);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading study:', err);
        setError('Failed to load study');
        setLoading(false);
      }
    };

    loadData();
  }, [studyId, fromCourse, courseId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.Loader className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
          <p className="text-gray-600">Loading study...</p>
        </div>
      </div>
    );
  }

  if (error || !study) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.X className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Study not found'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-900"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <StudyViewer
      key={study.id}
      study={study}
      userId={user?.uid}
      user={user}
      organization={organization}
      onLogout={user ? logout : undefined}
      initialProgress={startFresh ? 0 : (user?.completedStudyIds?.includes(study.id) ? 0 : (user?.studyProgress?.[study.id] || 0))}
      isPartOfCourse={!!courseContext}
      courseContext={courseContext || undefined}
      onExit={() => {
        if (courseContext) {
          navigate(`/course/${courseContext.courseId}`);
        } else {
          navigate(user ? '/dashboard' : '/gallery');
        }
      }}
      onCourseClick={courseContext ? () => navigate(`/course/${courseContext.courseId}`) : undefined}
      onStartNextStudy={courseContext?.nextStudy ? (nextStudyId) => {
        navigate(`/study/${nextStudyId}?from=course&courseId=${courseContext.courseId}&fresh=true`);
      } : undefined}
      onComplete={(newAwards) => {
        console.log('Study completed with awards:', newAwards);
      }}
    />
  );
};
